import { z } from 'zod';

import { createProposedActionsApi } from '@/shared/api/lotus';

import { defineToolSpec, toToolInputJsonSchema } from '../../types';
import {
  buildApplyModificationsErrorText,
  nonEmptyStringSchema,
  proposedActionPayloadSchema,
  toApiPayload,
  type ApiAddModification,
  type ApiAddModificationIntent,
} from './shared';
import { buildProposedActionExternalId } from './proposedActionIdempotency';

const inputSchema = z.object({
  link: z.discriminatedUnion('type', [
    z.object({ type: z.literal('request'), id: z.string().uuid() }),
    z.object({
      type: z.literal('patient'),
      request_title: nonEmptyStringSchema,
      request_message: nonEmptyStringSchema,
      request_urgency: z.enum(['Routine', 'Urgent']),
    }),
    z.object({
      type: z.literal('problem'),
      id: z.string().uuid(),
      request_title: nonEmptyStringSchema,
      request_message: nonEmptyStringSchema,
      request_urgency: z.enum(['Routine', 'Urgent']),
    }),
  ]),
  title: nonEmptyStringSchema,
  justification: nonEmptyStringSchema,
  payload: proposedActionPayloadSchema,
  alternative_payloads: z.array(proposedActionPayloadSchema).optional(),
  depends_on_action_external_ids: z.array(nonEmptyStringSchema).optional(),
});

type CreateProposedActionInput = z.infer<typeof inputSchema>;

const TOOL_NAME = 'create_proposed_action';

export function createCreateProposedActionToolSpec(patientUserId: string) {
  const api = createProposedActionsApi();

  return defineToolSpec({
    name: TOOL_NAME,
    inputSchema,
    getDefinition: () => ({
      name: TOOL_NAME,
      description:
        "Create one new proposed clinical action in the patient's care plan. " +
        'The action ID is generated automatically — do NOT provide one. ' +
        'Use an existing request ID when appropriate. For a patient or problem link, request_title, request_message, and request_urgency are mandatory; a request is created immediately and the action is linked to it. ' +
        'Provide exactly one payload matching a type exposed by the schema. For labs, follow the system lab-order instructions and use the required grouped-lab payload type. ' +
        'Example prescription payload: {"payload_tool_name":"draft_prescription","payload_id":"rx-1","payload_justification":"Start first-line therapy","dispensableDrugId":12345,"directions":"Take 1 tablet by mouth daily","quantity":30,"refills":0,"daysSupply":30}. ' +
        'Example follow-up payload: {"payload_tool_name":"send_follow_up","payload_id":"fu-1","payload_justification":"Communicate next steps","title":"Follow-up","content":"Please complete your labs this week."}. ' +
        'Example diagnose payload: {"payload_tool_name":"diagnose","payload_id":"dx-1","payload_justification":"Lab-confirmed clinician assessment","icd10Code":"E11.9","note":"Newly diagnosed T2DM, confirmed by A1c 8.9%."} (the ICD-10 description is resolved from the code automatically).',
      input_schema: toToolInputJsonSchema(inputSchema),
    }),
    async handle(input: CreateProposedActionInput, context) {
      try {
        if (context.originatingUserMessageId === undefined) {
          throw new Error('Cannot create an idempotent proposed action without a user message ID');
        }
        const link =
          input.link.type === 'problem'
            ? { type: 'problem' as const, id: input.link.id }
            : input.link.type === 'request'
              ? { type: 'request' as const, id: input.link.id }
              : { type: 'patient' as const };
        const modificationIntent: ApiAddModificationIntent = {
          type: 'add',
          link,
          title: input.title,
          payload: toApiPayload(input.payload),
          alternatives: input.alternative_payloads?.map(toApiPayload) ?? [],
          justification: input.justification,
          depends_on_action_external_ids: input.depends_on_action_external_ids ?? [],
        };
        const intentWithRequest = {
          ...modificationIntent,
          ...(input.link.type === 'request'
            ? {}
            : {
                request_title: input.link.request_title,
                request_message: input.link.request_message,
                request_urgency: input.link.request_urgency,
              }),
        };
        const externalId = await buildProposedActionExternalId(
          patientUserId,
          context.originatingUserMessageId,
          intentWithRequest
        );
        const modification: ApiAddModification = {
          ...intentWithRequest,
          external_id: externalId,
        };

        const result = await api.applyModifications(patientUserId, [modification]);
        return { output: { ...result, generated_external_id: externalId } };
      } catch (error: unknown) {
        return { errorText: buildApplyModificationsErrorText(error) };
      }
    },
  });
}
